/* ===========================================================================
   Add to cart -- reachability and a last-resort open -- 2026-08-16
   ===========================================================================
   Two jobs, both additive. hn-cart.js still owns the cart drawer; nothing here
   duplicates its work or changes what it does when it works.

     1. Measure Shopify's draft preview bar and lift the sticky add-to-cart
        footer clear of it. On a draft URL that bar covers the whole button, so
        the tap lands on Shopify's iframe and nothing is added at all -- the
        drawer is not failing to open, the add never happens. Published pages
        have no preview bar and are untouched.

     2. After any successful add to cart, make sure the drawer is open. This is
        a net, not the mechanism: hn-cart.js intercepts add buttons, add form
        submits, and cart writes it sees on fetch/XHR, and in every path
        measured it opens the drawer on its own, in which case this does
        nothing. It exists so that an add arriving by a route nobody enumerated
        still ends with the cart on screen, which is what the shop owner asked
        for in as many words.

   The net is deliberately narrow, because a drawer that opens when the shopper
   did not ask for it is worse than one that occasionally does not:

     * only /cart/add, never /cart/change or /cart/update -- editing quantities
       inside the drawer must not re-open anything;
     * only within 10s of a real user gesture, so the free-gift reconciliation
       that runs on page load cannot pop the drawer at a shopper who has just
       arrived;
     * never when the drawer is already open, which is the normal case.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var GESTURE_WINDOW_MS = 10000;
  var OPEN_CHECK_DELAY_MS = 1200;

  var lastGestureAt = 0;

  /* ------------------------------------------------ 1. the preview bar */

  function measurePreviewBar() {
    var wrapper = document.getElementById('PBarNextFrameWrapper')
      || document.getElementById('PBarNextFrame');
    var height = 0;

    if (wrapper) {
      var rect = wrapper.getBoundingClientRect();
      /* Only a bar actually parked at the bottom of the window is in the way.
         Shopify has shipped the preview bar collapsed, hidden and detached at
         various times, and lifting the button for a bar that is not there
         would leave a gap on the published site if this ever ran there. */
      if (rect.height > 0 && rect.bottom >= window.innerHeight - 4) {
        height = rect.height;
      }
    }

    var root = document.documentElement;
    if (height > 0) {
      root.style.setProperty('--mm-pbar-h', Math.round(height) + 'px');
      document.body.classList.add('mm-has-pbar');
    } else {
      root.style.removeProperty('--mm-pbar-h');
      document.body.classList.remove('mm-has-pbar');
    }
  }

  function watchPreviewBar() {
    measurePreviewBar();
    /* The bar is injected late and can resize when its contents wrap, so a
       single measurement at load is not enough. */
    var tries = 0;
    var timer = window.setInterval(function () {
      measurePreviewBar();
      if (++tries >= 20) window.clearInterval(timer);
    }, 500);
    window.addEventListener('resize', measurePreviewBar);
    window.addEventListener('orientationchange', measurePreviewBar);
  }

  /* ------------------------------------------------ 2. the last-resort open */

  function drawerIsOpen() {
    var drawer = document.getElementById('hn-cart-drawer');
    return !!drawer && drawer.classList.contains('is-open');
  }

  function ensureOpenAfterAdd() {
    if (Date.now() - lastGestureAt > GESTURE_WINDOW_MS) return;
    window.setTimeout(function () {
      if (drawerIsOpen()) return;
      if (!document.getElementById('hn-cart-drawer')) return;
      if (!window.HNCart || typeof window.HNCart.open !== 'function') return;
      window.HNCart.open();
    }, OPEN_CHECK_DELAY_MS);
  }

  function patchNetwork() {
    /* Wraps whatever hn-cart.js already installed rather than replacing it --
       this file is loaded after it, so window.fetch here is hn-cart's wrapper
       and its own behaviour still runs first. */
    var priorFetch = window.fetch;
    if (typeof priorFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = priorFetch.apply(this, arguments);
        if (ADD_RE.test(url)) {
          promise.then(function (response) {
            if (response && response.ok) ensureOpenAfterAdd();
          }).catch(function () {});
        }
        return promise;
      };
    }

    var priorOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__mmUrl = url;
      return priorOpen.apply(this, arguments);
    };

    var priorSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300 && ADD_RE.test(xhr.__mmUrl || '')) {
          ensureOpenAfterAdd();
        }
      });
      return priorSend.apply(this, arguments);
    };
  }

  /* ------------------------------- 3. adding must never leave the page */

  /* A programmatic form.submit() fires NO submit event, so hn-cart.js's
     interceptForms() cannot see it — that is how an add-to-cart form ends up
     navigating to /cart even though every submit listener on the page is
     trying to prevent exactly that. hn-cart's own failure path used to call it
     and no longer does; this covers any other caller, in the theme or in an
     app, that nobody has found yet.

     Routed, not blocked. The add is retried over the API and the drawer is
     opened; only if that also fails does the native submit run after all. So
     nothing that works today can stop working because of this — the worst case
     is the old behaviour, and the normal case is that the shopper stays put.  */
  function keepAddsOnPage() {
    var nativeSubmit = HTMLFormElement.prototype.submit;

    HTMLFormElement.prototype.submit = function () {
      var form = this;
      var action = form.getAttribute('action') || '';
      var self = this;
      var args = arguments;

      if (!/\/cart\/add/.test(action) || form.hasAttribute('data-hn-ignore')) {
        return nativeSubmit.apply(self, args);
      }

      try {
        fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        }).then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          if (window.HNCart && typeof window.HNCart.refresh === 'function') {
            window.HNCart.refresh();
          }
          if (window.HNCart && typeof window.HNCart.open === 'function') {
            window.HNCart.open();
          }
        }).catch(function (err) {
          console.warn('[mm-atc] add fallback', err && err.message);
          nativeSubmit.apply(self, args);
        });
      } catch (err) {
        return nativeSubmit.apply(self, args);
      }
    };
  }

  /* ------------------------- 4. a sold-out variant must not be addable */

  /* The theme renders BOTH an "Out of stock" link and an Add to cart button and
     expects its own script to swap them per selected variant. On this shop that
     swap does not happen: vitamin-d3-k2-drops has `available: false` on the
     product and on all four variants in the page's own ProductJson, and every
     Add to cart button still rendered enabled. Tapping one reached Shopify,
     came back "Cannot find variant", and the shopper got nothing.

     Truth comes from the `ProductJson-*` script the theme already prints —
     server-rendered from the same Liquid that drew the page, so it cannot
     disagree with it the way a separate API call can drift.

     This FAILS OPEN, deliberately and in three places: no ProductJson, no id
     field, or a variant id the map has never heard of, and the button is left
     exactly as it was. The cost of a wrong "sold out" is a product nobody can
     buy, which is far worse than the fault being fixed. Only a variant this
     page positively states is unavailable is ever disabled. */
  var availabilityCache;

  function availabilityMap() {
    /* Parsed once. These scripts are server-rendered into the page and never
       change after it loads, and the product JSON here is ~15KB — re-parsing it
       on every click, which is what the first version of this did, is real work
       on the main thread for an answer that cannot have changed.

       Only a real map is cached. Caching the absence too, which is what
       `!== undefined` did, means one early call that found no ProductJson locks
       the answer to "unknown" for the life of the page and every later sync
       returns before doing anything — silently, since not-knowing is the safe
       branch. Retrying costs one querySelectorAll on a page that has no product
       JSON at all. */
    if (availabilityCache) return availabilityCache;

    var map = null;
    var scripts = document.querySelectorAll('script[id^="ProductJson-"]');
    Array.prototype.forEach.call(scripts, function (node) {
      var data;
      try { data = JSON.parse(node.textContent); } catch (err) { return; }
      if (!data || !data.variants || !data.variants.length) return;
      data.variants.forEach(function (variant) {
        if (!variant || variant.id == null) return;
        if (typeof variant.available !== 'boolean') return;
        if (!map) map = {};
        map[String(variant.id)] = variant.available;
      });
    });
    availabilityCache = map;
    return map;
  }

  function syncSoldOut() {
    var map = availabilityMap();
    if (!map) return;

    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    Array.prototype.forEach.call(forms, function (form) {
      var field = form.querySelector('[name="id"]');
      if (!field) return;
      var id = String(field.value || '');
      if (!id || !Object.prototype.hasOwnProperty.call(map, id)) return;

      var soldOut = map[id] === false;
      if (soldOut) form.classList.add('mm-soldout');
      else form.classList.remove('mm-soldout');

      var buttons = form.querySelectorAll('button.single_add_to_cart_button, button[name="add"]');
      Array.prototype.forEach.call(buttons, function (button) {
        /* disabled is what actually stops the add: hn-cart.js's click
           interceptor returns early on a disabled button, and a disabled
           submit cannot submit its form either. The class below is only what
           makes that state visible. */
        button.disabled = soldOut;
        if (soldOut) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    });
  }

  function watchSoldOut() {
    syncSoldOut();

    /* The pack selector on this shop is jQuery and sets select[name="id"]
       programmatically, which does not fire a change event on every path. So
       the state is re-read after any interaction as well as on change, and
       again a moment later, because the selector's own handler may not have run
       yet when the click finishes bubbling.

       Debounced rather than fired three times per click: a listener on every
       click in the document is a place where careless work becomes jank on the
       one interaction the shopper is judging the shop by. */
    var early = 0;
    var late = 0;
    var resync = function () {
      window.clearTimeout(early);
      window.clearTimeout(late);
      early = window.setTimeout(syncSoldOut, 60);
      late = window.setTimeout(syncSoldOut, 400);
    };

    document.addEventListener('change', resync, true);
    document.addEventListener('click', resync, true);
    window.setTimeout(syncSoldOut, 1200);
  }

  function trackGestures() {
    ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
      document.addEventListener(type, function () { lastGestureAt = Date.now(); }, true);
    });
  }

  function start() {
    trackGestures();
    patchNetwork();
    keepAddsOnPage();
    watchSoldOut();
    watchPreviewBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
